import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    MAX_COMMENT_LENGTH,
    type TaskComment,
    type TaskOccurrenceStatus,
    type TaskWithOccurrences,
} from "@dashboard/api/task";
import { faPlus } from "@fortawesome/sharp-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useHseSummary } from "@dashboard/hooks/useHseSummary";
import { useState } from "react";
import { useTaskComments, useCreateTaskComment } from "@dashboard/hooks/useTaskCommentQuery";
import { TaskCommentItem } from "./TaskCommentItem";
import { Button } from "frontend-components";
import { CharacterCount } from "@dashboard/components/CharacterCount";
import { useUpdateTaskOccurrenceStatus } from "@dashboard/hooks/useUpdateTaskOccurrenceStatus";
import { TaskImageModal } from "./TaskImageModal";
import { TaskImageThumbnails } from "./TaskImageThumbnails";
import { Can } from "@dashboard/casl/taskAbility";
import { subject } from "@casl/ability";
import { usePrintMode } from "@dashboard/contexts/printModeContext";

type RadioVariant = "yes" | "no";

function StatusRadio({
    variant,
    active,
    interactive,
    onClick,
    disabled,
    label,
    ariaLabel,
}: {
    variant: RadioVariant;
    active: boolean;
    interactive: boolean;
    onClick?: () => void;
    disabled?: boolean;
    label: string;
    ariaLabel: string;
}) {
    const fillClass = variant === "yes" ? "bg-chameleon-green" : "bg-chameleon-pink";
    const ringClass = variant === "yes" ? "border-chameleon-green" : "border-chameleon-pink";

    const dot = (
        <span
            className={`inline-flex items-center justify-center size-4 rounded-full border bg-transparent ${
                active ? ringClass : "border-compendia-border"
            }`}
        >
            {active && <span className={`size-2 rounded-full ${fillClass}`} />}
        </span>
    );

    if (!interactive) {
        return (
            <span className="inline-flex items-center gap-2 text-sm text-compendia-foreground">
                {dot}
                {label}
            </span>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            className="inline-flex items-center gap-2 text-sm text-compendia-foreground cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        >
            {dot}
            {label}
        </button>
    );
}

export default function TaskItem({ task }: { task: TaskWithOccurrences }) {
    const { t } = useTranslation();
    const isPrintMode = usePrintMode();
    // Only needed for the "Create issue" link, which print mode doesn't show.
    const { data: summary } = useHseSummary(!isPrintMode);

    const updateStatus = useUpdateTaskOccurrenceStatus();

    const occurrence = task.occurrences[0];
    const currentStatus: TaskOccurrenceStatus | undefined = occurrence?.status;

    const handleStatusChange = (status: TaskOccurrenceStatus) => {
        if (!occurrence || currentStatus === status) return;
        updateStatus.mutate({ id: occurrence.id, status });
    };

    const createIssueUrl = (() => {
        if (Array.isArray(summary?.links) || !summary?.links.issues) return null;
        const url = new URL(`${summary.links.issues.replace("#issues", "issue")}/create`);
        if (task.title) url.searchParams.set("title", task.title);
        return url.toString();
    })();

    const [showCommentInput, setShowCommentInput] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [showAllCommentsState, setShowAllComments] = useState(false);
    const showAllComments = showAllCommentsState || isPrintMode;
    const INITIAL_COMMENTS_TO_SHOW = 2;

    const { data: initialCommentsData } = useTaskComments(task.id, {
        skip: 0,
        take: INITIAL_COMMENTS_TO_SHOW,
    });

    const totalComments = initialCommentsData?.total || 0;

    const { data: allCommentsData } = useTaskComments(
        task.id,
        { skip: 0, take: totalComments },
        { enabled: showAllComments && totalComments > INITIAL_COMMENTS_TO_SHOW },
    );

    const createCommentMutation = useCreateTaskComment();

    const commentsData = showAllComments && allCommentsData ? allCommentsData : initialCommentsData;
    const comments = commentsData?.data || [];
    const hasMoreComments = totalComments > INITIAL_COMMENTS_TO_SHOW;
    const remainingCount = totalComments - INITIAL_COMMENTS_TO_SHOW;

    const handleSaveComment = async () => {
        if (!commentText.trim()) return;
        try {
            await createCommentMutation.mutateAsync({
                taskId: task.id,
                data: { comment: commentText },
            });
            setCommentText("");
            setShowCommentInput(false);
        } catch (error) {
            console.error("Failed to create comment:", error);
        }
    };

    const handleCancelComment = () => {
        setCommentText("");
        setShowCommentInput(false);
    };

    return (
        <div className="bg-compendia-card border border-compendia-border rounded-lg p-4 flex flex-col gap-3 break-inside-avoid">
            <h2 className="text-sm font-medium text-compendia-foreground overflow-hidden text-ellipsis">
                {task.title}
            </h2>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <Can I="update" this={subject("TaskOccurrence", { task })} passThrough>
                    {(access) => (
                        <StatusRadio
                            variant="yes"
                            active={currentStatus === "DONE"}
                            interactive={access}
                            onClick={() => handleStatusChange("DONE")}
                            disabled={updateStatus.isPending}
                            label={t("list.taskOk")}
                            ariaLabel={t("list.markTaskDone")}
                        />
                    )}
                </Can>
                <Can I="update" this={subject("TaskOccurrence", { task })} passThrough>
                    {(access) => (
                        <StatusRadio
                            variant="no"
                            active={currentStatus === "BLOCKED"}
                            interactive={access}
                            onClick={() => handleStatusChange("BLOCKED")}
                            disabled={updateStatus.isPending}
                            label={t("list.taskNotOk")}
                            ariaLabel={t("list.markTaskBlocked")}
                        />
                    )}
                </Can>
                <Can I="create" this={subject("TaskComment", { task })}>
                    <button
                        type="button"
                        onClick={() => setShowCommentInput(!showCommentInput)}
                        className="text-xs text-compendia-foreground hover:underline cursor-pointer inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-1 text-[10px]" />
                        {t("list.add_comment")}
                    </button>
                    <TaskImageModal taskId={task.id} />
                </Can>
                {createIssueUrl && !isPrintMode && (
                    <a
                        href={createIssueUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-compendia-muted hover:text-compendia-foreground inline-flex items-center"
                    >
                        {t("list.create_issue")}
                    </a>
                )}
            </div>

            {showCommentInput && (
                <Can I="create" this={subject("TaskComment", { task })}>
                    <div className="flex flex-col gap-2">
                        <textarea
                            className="w-full p-3 bg-compendia-card border border-compendia-border rounded-none text-sm text-compendia-foreground resize-none focus:outline-none focus:border-border-focus"
                            placeholder={t("list.writeYourCommentHere")}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            rows={2}
                            autoFocus
                        />
                        <div className="flex justify-between items-center">
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="submit"
                                    size="submit"
                                    onClick={handleSaveComment}
                                    disabled={
                                        !commentText.trim() ||
                                        commentText.length > MAX_COMMENT_LENGTH ||
                                        createCommentMutation.isPending
                                    }
                                >
                                    {t("common.save")}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancelComment}
                                >
                                    {t("common.cancel")}
                                </Button>
                            </div>
                            <CharacterCount
                                currentLength={commentText.length}
                                maxLength={MAX_COMMENT_LENGTH}
                            />
                        </div>
                    </div>
                </Can>
            )}

            {comments.length > 0 && (
                <div className="flex flex-col gap-2">
                    {comments.map((comment: TaskComment) => (
                        <TaskCommentItem key={comment.id} comment={comment} task={task} />
                    ))}

                    {hasMoreComments && !showAllComments && (
                        <button
                            type="button"
                            onClick={() => setShowAllComments(true)}
                            className="text-xs text-compendia-foreground hover:underline font-medium text-left"
                        >
                            {t("list.viewMoreComments", { count: remainingCount })}
                        </button>
                    )}

                    {showAllComments && hasMoreComments && !isPrintMode && (
                        <button
                            type="button"
                            onClick={() => setShowAllComments(false)}
                            className="text-xs text-compendia-foreground hover:underline font-medium text-left"
                        >
                            {t("list.showLessComments")}
                        </button>
                    )}
                </div>
            )}

            <TaskImageThumbnails taskId={task.id} task={task} />
        </div>
    );
}
